import { useEffect, useRef, useState } from "react";

interface UseSyncValuesProps<P, L> {
  parentValue: P;
  setParentValue: (value: P) => void;
  transformParentValue: (value: P) => L;
  localValue: L;
  setLocalValue: (value: L) => void;
  transformLocalValue: (value: L) => P;
}

export const useSyncValues = <P, L>({
  parentValue,
  setParentValue,
  transformParentValue,
  localValue,
  setLocalValue,
  transformLocalValue,
}: UseSyncValuesProps<P, L>) => {
  const isFirstRenderRef = useRef(true);
  const isFirstRender = isFirstRenderRef.current;
  isFirstRenderRef.current = false;

  const ignoreNextParentUpdate = useRef(false);
  const ignoreNextLocalUpdate = useRef(false);

  const [updatedParent, setUpdatedParent] = useState(0);

  useEffect(() => {
    if (isFirstRender) return;

    if (ignoreNextParentUpdate.current) {
      ignoreNextParentUpdate.current = false;
    } else {
      ignoreNextLocalUpdate.current = true;
      setLocalValue(transformParentValue(parentValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentValue]);

  // sync local value to parent on first render
  useEffect(() => {
    if (!isFirstRender) return;

    ignoreNextLocalUpdate.current = true;
    setLocalValue(transformParentValue(parentValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentValue]);

  useEffect(() => {
    if (isFirstRender) return;

    if (ignoreNextLocalUpdate.current) {
      ignoreNextLocalUpdate.current = false;
    } else {
      ignoreNextParentUpdate.current = true;
      setParentValue(transformLocalValue(localValue));
      setUpdatedParent((prev) => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localValue]);

  /**
   * This effect exists because setting the parent value with a new local value
   * may not necessarily change the parent value and trigger a re-render. In
   * that case, the `ignoreNextParentUpdate` flag needs to be manually updated.
   */
  useEffect(() => {
    if (ignoreNextParentUpdate.current) {
      ignoreNextParentUpdate.current = false;
    }
  }, [updatedParent]);
};
